import React, { useState, useEffect } from "react";
import AdminNavBar from "../components/AdminNavBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../components/Layout";

interface CoaTestResult {
  id: number;
  testType: string;
  pdfUrl: string;

  // Thumbnail (first page of PDF, optional)
  thumbnailUrl?: string;

  // Potency
  totalTHCPercent?: number;
  totalCBDPercent?: number;
  totalCannabinoidsPercent?: number;
  totalTHCMgPerG?: number;
  totalCannabinoidsMgPerG?: number;

  // Terpenes
  totalTerpenesPercent?: number;
  dominantTerpene1Name?: string;
  dominantTerpene1Percent?: number;

  // Pathogens
  pathogenOverallResult?: string;
  pathogensPass?: boolean;
}

interface CoaSample {
  id: number;
  sampleId: string;
  sampleName?: string;
  clientName?: string;
  tests: CoaTestResult[];
}

export default function ManageCoas() {
  const [samples, setSamples] = useState<CoaSample[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchCoaSamples();
  }, []);

  const fetchCoaSamples = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/coa/samples`
      );
      if (response.ok) {
        const data: CoaSample[] = await response.json();
        setSamples(data);
      } else {
        throw new Error("Failed to fetch COA samples");
      }
    } catch (error) {
      console.error("Failed to fetch COA samples:", error);
      toast.error("Failed to fetch COA samples");
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/coa/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload file");
      }

      const data = (await response.json()) as any;

      const first = Array.isArray(data.parseResult)
        ? data.parseResult[0]
        : null;

      if (first && first.skipped) {
        toast.info(
          first.reason ||
            "This COA test already exists for this sample and test type. No new record created."
        );
      } else if (first && first.success) {
        toast.success("COA uploaded and parsed successfully.");
      } else {
        toast.success("File uploaded successfully. Parsing has been triggered.");
      }

      await fetchCoaSamples();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast.error(`Upload failed: ${errorMessage}`);
      console.error("Failed to upload file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout>
      <AdminNavBar />
      <div className="pt-[110px] min-h-screen bg-fractal-pattern-3 bg-cover bg-fixed p-10 relative">
        <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-10">
          Manage Certificates of Analysis (COAs)
        </h1>

        <div className="text-center mb-8">
          <label className="bg-gradient-to-r from-green-400 to-green-600 text-white font-bold py-3 px-8 rounded-lg shadow-md hover:from-green-500 hover:to-green-700 transition duration-300 cursor-pointer">
            {isUploading ? "Uploading..." : "Upload COA PDF"}
            <input
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
              accept=".pdf"
            />
          </label>
          <p className="text-sm text-gray-600 mt-2">
            Upload the COA PDF as you received it from the testing facility.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {samples.map((sample) => (
            <div key={sample.id} className="bg-white p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-bold mb-2">
                {sample.sampleName || sample.sampleId}
              </h2>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Sample ID:</strong> {sample.sampleId}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Client:</strong> {sample.clientName || "N/A"}
              </p>

              <div className="space-y-4">
                {sample.tests.map((test) => {
                // Go through backend proxy so we don't hit Nextcloud directly
                const proxiedPdfUrl = `${process.env.REACT_APP_API_URL}/api/coa/file?url=${encodeURIComponent(
                  test.pdfUrl
                )}`;

                const proxiedThumbnailUrl = test.thumbnailUrl
                  ? `${process.env.REACT_APP_API_URL}/api/coa/file?url=${encodeURIComponent(
                      test.thumbnailUrl
                    )}`
                  : null;


                  return (
                    <div key={test.id} className="border-t pt-4">
                      <h3 className="font-semibold text-md">{test.testType}</h3>
                      <a
                        href={proxiedPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline text-sm"
                      >
                        View PDF
                      </a>

                      {proxiedThumbnailUrl && (
                        <div className="mt-2">
                          <img
                            src={proxiedThumbnailUrl}
                            alt={`${sample.sampleName || sample.sampleId} ${
                              test.testType
                            } COA`}
                            className="w-full rounded border"
                          />
                        </div>
                      )}

                      {test.testType === "POTENCY" && (
                        <div className="text-xs mt-2 space-y-1">
                          <p>
                            <strong>Total THC:</strong>{" "}
                            {test.totalTHCPercent != null
                              ? `${test.totalTHCPercent.toFixed(2)}%`
                              : "—"}
                          </p>
                          <p>
                            <strong>Total CBD:</strong>{" "}
                            {test.totalCBDPercent != null
                              ? `${test.totalCBDPercent.toFixed(2)}%`
                              : "—"}
                          </p>
                          <p>
                            <strong>Total Cannabinoids:</strong>{" "}
                            {test.totalCannabinoidsPercent != null
                              ? `${test.totalCannabinoidsPercent.toFixed(2)}%`
                              : "—"}
                          </p>
                          <p>
                            <strong>THC (mg/g):</strong>{" "}
                            {test.totalTHCMgPerG != null
                              ? `${test.totalTHCMgPerG.toFixed(1)}`
                              : "—"}
                          </p>
                          <p>
                            <strong>Total Cannabinoids (mg/g):</strong>{" "}
                            {test.totalCannabinoidsMgPerG != null
                              ? `${test.totalCannabinoidsMgPerG.toFixed(1)}`
                              : "—"}
                          </p>
                        </div>
                      )}

                      {test.testType === "TERPENES" && (
                        <div className="text-xs mt-2 space-y-1">
                          <p>
                            <strong>Total Terpenes:</strong>{" "}
                            {test.totalTerpenesPercent != null
                              ? `${test.totalTerpenesPercent.toFixed(2)}%`
                              : "—"}
                          </p>
                          <p>
                            <strong>Dominant Terpene:</strong>{" "}
                            {test.dominantTerpene1Name || "N/A"}
                            {test.dominantTerpene1Percent != null
                              ? ` (${test.dominantTerpene1Percent.toFixed(
                                  2
                                )}%)`
                              : ""}
                          </p>
                        </div>
                      )}

                      {test.testType === "PATHOGENS" && (
                        <div className="text-xs mt-2 space-y-1">
                          <p>
                            <strong>Overall:</strong>{" "}
                            {test.pathogenOverallResult || "—"}
                          </p>
                          <p>
                            <strong>Result:</strong>{" "}
                            {test.pathogensPass == null
                              ? "—"
                              : test.pathogensPass
                              ? "PASS"
                              : "FAIL"}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {samples.length === 0 && (
          <div className="text-center mt-10 text-gray-600 text-lg">
            <p>No COA samples available. Upload a COA to get started.</p>
          </div>
        )}

        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
        />
      </div>
    </Layout>
  );
}
